"use client"

import * as React from "react"
import { Briefcase, Pencil, CheckCircle2, AlertCircle } from "lucide-react"
import { TabsContent } from "@/components/ui/tabs"
import { SupabaseUpload } from "@/components/SupabaseUpload"
import { Account } from "@/types/workspace"
import { toast } from "sonner"

// Validação de CNPJ
function isValidCNPJ(cnpj: string): boolean {
  const clean = cnpj.replace(/\D/g, "")
  if (clean.length !== 14) return false
  if (/^(\d)\1{13}$/.test(clean)) return false

  let size = clean.length - 2
  let numbers = clean.substring(0, size)
  const digits = clean.substring(size)
  let sum = 0
  let pos = size - 7
  for (let i = size; i >= 1; i--) {
    sum += Number(numbers.charAt(size - i)) * pos--
    if (pos < 2) pos = 9
  }
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11)
  if (result !== Number(digits.charAt(0))) return false

  size = size + 1
  numbers = clean.substring(0, size)
  sum = 0
  pos = size - 7
  for (let i = size; i >= 1; i--) {
    sum += Number(numbers.charAt(size - i)) * pos--
    if (pos < 2) pos = 9
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11)
  if (result !== Number(digits.charAt(1))) return false

  return true
}

// Máscara de CNPJ
function maskCNPJ(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 14)
  if (digits.length <= 2) return digits
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`
}

export interface CompanySettingsTabProps {
  currentAccount: Account | null
  companyName: string
  setCompanyName: (val: string) => void
  companyDocument: string
  setCompanyDocument: (val: string) => void
  companyLogoUrl: string
  setCompanyLogoUrl: (val: string) => void
  companyBannerUrl?: string
  setCompanyBannerUrl?: (val: string) => void
  companyPrimaryColor?: string
  setCompanyPrimaryColor?: (val: string) => void
  companyCustomDomain?: string
  setCompanyCustomDomain?: (val: string) => void
  handleSaveCompany?: (data?: any) => Promise<any> | void
  profileRole?: string
  t: Record<string, string>
}

export function CompanySettingsTab({
  currentAccount,
  companyName,
  setCompanyName,
  companyDocument,
  setCompanyDocument,
  companyLogoUrl,
  setCompanyLogoUrl,
  companyBannerUrl: propCompanyBannerUrl,
  setCompanyBannerUrl: propSetCompanyBannerUrl,
  handleSaveCompany,
  profileRole,
  t,
}: CompanySettingsTabProps) {
  const isAdmin = profileRole === "admin" || profileRole === "owner"
  const [isEditingCompanyProfile, setIsEditingCompanyProfile] = React.useState(false)
  const [companyBio, setCompanyBio] = React.useState(currentAccount?.bio || "Transformando a inovação digital em ecossistemas inteligentes de alta performance.")
  const [companyBannerUrl, setCompanyBannerUrl] = React.useState(propCompanyBannerUrl || currentAccount?.bannerUrl || "")
  const [companyCategory, setCompanyCategory] = React.useState(currentAccount?.category || "Tecnologia & Softwares SaaS")
  const [companyLocation, setCompanyLocation] = React.useState(currentAccount?.location || "São Paulo, Brasil")

  React.useEffect(() => {
    if (propCompanyBannerUrl !== undefined) {
      setCompanyBannerUrl(propCompanyBannerUrl)
    } else if (currentAccount?.bannerUrl) {
      setCompanyBannerUrl(currentAccount.bannerUrl)
    }
  }, [propCompanyBannerUrl, currentAccount?.bannerUrl])

  React.useEffect(() => {
    if (currentAccount?.bio) setCompanyBio(currentAccount.bio)
    if (currentAccount?.category) setCompanyCategory(currentAccount.category)
    if (currentAccount?.location) setCompanyLocation(currentAccount.location)
  }, [currentAccount?.bio, currentAccount?.category, currentAccount?.location])

  const handleUpdateBanner = (url: string) => {
    setCompanyBannerUrl(url)
    propSetCompanyBannerUrl?.(url)
  }

  const handleRemoveBanner = () => {
    setCompanyBannerUrl("")
    propSetCompanyBannerUrl?.("")
  }

  const handleUpdateLogo = (url: string) => {
    setCompanyLogoUrl(url)
  }

  const handleRemoveLogo = () => {
    setCompanyLogoUrl("")
  }

  const handleCancelEditing = () => {
    if (currentAccount) {
      setCompanyName(currentAccount.name || "")
      setCompanyLogoUrl(currentAccount.logoUrl || "")
      setCompanyBannerUrl(currentAccount.bannerUrl || "")
      propSetCompanyBannerUrl?.(currentAccount.bannerUrl || "")
      setCompanyDocument(currentAccount.document || "")
      setCompanyBio(currentAccount.bio || "")
      setCompanyCategory(currentAccount.category || "Tecnologia & Softwares SaaS")
      setCompanyLocation(currentAccount.location || "São Paulo, Brasil")
    }
    setIsEditingCompanyProfile(false)
  }

  const isCnpjValid = !companyDocument.trim() || isValidCNPJ(companyDocument)

  return (
    <TabsContent value="company" className="outline-none space-y-6">
      <div>
        <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
          <Briefcase className="size-5 text-[#004EB1]" />
          Perfil Corporativo & Identidade
        </h3>
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-sans">
          Gerencie a presença institucional, marca e informações cadastrais da sua empresa.
        </p>
      </div>

      {/* CARD DE PERFIL DE REDE SOCIAL COM EDIÇÃO INLINE */}
      <div className="border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden shadow-lg font-sans transition-all">
        {/* Banner de Capa com Degradê Oficial Bold */}
        <div
          className="relative h-44 w-full overflow-hidden group shadow-inner"
          style={{
            background: "linear-gradient(135deg, #004EB1 0%, #003780 45%, #08F36C 100%)",
          }}
        >
          {companyBannerUrl ? (
            <img src={companyBannerUrl} alt="Capa Corporativa" className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
              <span className="text-white/30 text-3xl font-extrabold tracking-widest uppercase select-none">
                {companyName || "BOLD WORKSPACE"}
              </span>
            </div>
          )}
        </div>

        {/* Seção Principal do Perfil */}
        <div className="p-6 pt-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-zinc-150 dark:border-zinc-800">
            {/* Logo / Avatar com Borda (Sobreposto à Capa) */}
            <div className="relative group -mt-16">
              <div className="size-24 rounded-2xl border-4 border-white dark:border-zinc-900 bg-zinc-100 dark:bg-zinc-800 shadow-xl overflow-hidden flex items-center justify-center font-bold text-3xl text-zinc-700 dark:text-zinc-300">
                {companyLogoUrl ? (
                  <img src={companyLogoUrl} alt={companyName} className="w-full h-full object-cover" />
                ) : (
                  <span>{(companyName || "B")[0].toUpperCase()}</span>
                )}
              </div>
              <span className="absolute bottom-1 right-1 size-4 bg-emerald-500 border-2 border-white dark:border-zinc-900 rounded-full" title="Conta Verificada" />
            </div>

            {/* Botão de Alternar Modo de Edição (Ícone de Lápis) + Status */}
            <div className="flex items-center gap-2.5 flex-wrap pt-2 sm:pt-0">
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => {
                    if (isEditingCompanyProfile) {
                      handleCancelEditing()
                    } else {
                      setIsEditingCompanyProfile(true)
                    }
                  }}
                  className={`h-9 px-3 rounded-xl border flex items-center gap-2 transition-all cursor-pointer shadow-sm text-xs font-bold ${isEditingCompanyProfile
                    ? "bg-amber-500 text-white border-amber-600 scale-105"
                    : "bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 border-zinc-200 dark:border-zinc-700 hover:border-[#004EB1]"
                    }`}
                  title={isEditingCompanyProfile ? "Cancelar" : "Editar"}
                >
                  <Pencil className="size-3.5" />
                  <span>{isEditingCompanyProfile ? "Cancelar" : "Editar"}</span>
                </button>
              )}
              <span className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 text-[11px] font-bold px-3 py-1.5 rounded-xl">
                Slug: @{currentAccount?.slug || "workspace"}
              </span>
            </div>
          </div>

          {/* Conteúdo do Perfil / Edição Inline */}
          <div className="space-y-4 pt-2">
            {isAdmin && isEditingCompanyProfile ? (
              <div className="space-y-4 animate-in fade-in duration-200 bg-zinc-50 dark:bg-zinc-950 p-5 rounded-xl border border-zinc-200 dark:border-zinc-800">
                {/* Seção de Mídia e Imagens */}
                <div className="space-y-2 border-b border-zinc-200 dark:border-zinc-800 pb-4">
                  <h4 className="text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Imagens da Marca</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-bold text-zinc-500 uppercase mb-1 block">Logo</label>
                      <SupabaseUpload
                        label="Alterar Logo"
                        currentUrl={companyLogoUrl}
                        cropShape="rounded"
                        aspectRatio={1}
                        onUploadSuccess={handleUpdateLogo}
                        onRemove={handleRemoveLogo}
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-zinc-500 uppercase mb-1 block">Banner</label>
                      <SupabaseUpload
                        label="Alterar Capa"
                        currentUrl={companyBannerUrl}
                        cropShape="rect"
                        aspectRatio={3}
                        disableCrop={false}
                        onUploadSuccess={handleUpdateBanner}
                        onRemove={handleRemoveBanner}
                      />
                    </div>
                  </div>
                </div>

                {/* Seção de Dados Principais */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-bold text-zinc-500 uppercase">Nome Comercial</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Nome da Empresa"
                      className="w-full text-xs text-zinc-800 dark:text-zinc-200 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-lg px-3 py-2 focus:outline-none focus:border-[#004EB1]"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-zinc-500 uppercase flex items-center justify-between">
                      <span>CNPJ</span>
                      {companyDocument && !isCnpjValid && (
                        <span className="text-red-500 text-[10px] lowercase font-normal flex items-center gap-1">
                          <AlertCircle className="size-3" /> CNPJ inválido
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={companyDocument}
                      onChange={(e) => setCompanyDocument(maskCNPJ(e.target.value))}
                      placeholder="00.000.000/0000-00"
                      maxLength={18}
                      className={`w-full text-xs text-zinc-800 dark:text-zinc-200 bg-white dark:bg-zinc-900 border rounded-lg px-3 py-2 focus:outline-none font-mono ${companyDocument && !isCnpjValid
                        ? "border-red-500 focus:border-red-500"
                        : "border-zinc-300 dark:border-zinc-700 focus:border-[#004EB1]"
                        }`}
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-zinc-500 uppercase">Setor / Categoria</label>
                    <input
                      type="text"
                      value={companyCategory}
                      onChange={(e) => setCompanyCategory(e.target.value)}
                      placeholder="Ex: Tecnologia, Finanças, etc."
                      className="w-full text-xs text-zinc-800 dark:text-zinc-200 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-lg px-3 py-2 focus:outline-none focus:border-[#004EB1]"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-zinc-500 uppercase">Localização / Sede</label>
                    <input
                      type="text"
                      value={companyLocation}
                      onChange={(e) => setCompanyLocation(e.target.value)}
                      placeholder="Ex: São Paulo, Brasil"
                      className="w-full text-xs text-zinc-800 dark:text-zinc-200 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-lg px-3 py-2 focus:outline-none focus:border-[#004EB1]"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-zinc-500 uppercase">Apresentação / Bio Corporativa</label>
                  <textarea
                    rows={2}
                    value={companyBio}
                    onChange={(e) => setCompanyBio(e.target.value)}
                    placeholder="Descrição da empresa..."
                    className="w-full text-xs text-zinc-800 dark:text-zinc-200 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-lg px-3 py-2 focus:outline-none focus:border-[#004EB1] resize-none"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200 dark:border-zinc-800">
                  <button
                    type="button"
                    onClick={handleCancelEditing}
                    className="px-4 py-2 text-xs font-semibold text-zinc-600 dark:text-zinc-300 bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 rounded-lg transition-colors cursor-pointer"
                  >
                    Fechar
                  </button>
                  {handleSaveCompany && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (companyDocument.trim() && !isValidCNPJ(companyDocument)) {
                          toast.error("Por favor, insira um CNPJ válido.")
                          return
                        }
                        await handleSaveCompany({
                          name: companyName,
                          logoUrl: companyLogoUrl,
                          bannerUrl: companyBannerUrl,
                          document: companyDocument,
                          bio: companyBio,
                          category: companyCategory,
                          location: companyLocation,
                        })
                        setIsEditingCompanyProfile(false)
                      }}
                      className="px-4 py-2 text-xs font-semibold text-white bg-[#004EB1] hover:bg-[#003780] rounded-lg transition-colors cursor-pointer shadow-sm"
                    >
                      Salvar Alterações
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <h2 className="text-xl font-bold text-zinc-900 dark:text-white tracking-tight">
                  {companyName || "Nome da Sua Empresa"}
                </h2>
                <p className="text-xs font-bold text-[#004EB1] dark:text-blue-400 flex items-center gap-2">
                  <span>{companyCategory || "Sem Categoria Especificada"}</span>
                  <span>•</span>
                  <span className="text-zinc-500 dark:text-zinc-400 font-medium">{companyLocation || "Localização não informada"}</span>
                  {companyDocument && (
                    <>
                      <span>•</span>
                      <span className="text-zinc-400 dark:text-zinc-500 font-mono text-[11px]">CNPJ: {companyDocument}</span>
                    </>
                  )}
                </p>
                <p className="text-xs text-zinc-600 dark:text-zinc-300 max-w-2xl pt-1 leading-relaxed">
                  {companyBio || "Nenhuma biografia ou apresentação cadastrada ainda. Clique no botão de editar acima para personalizar."}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </TabsContent>
  )
}
